import { useState, useEffect, useMemo } from 'react';
import {
    Stack,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    IconButton,
    Typography
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { createSubCategory, getSubCategoriesById, editSubCategoryById } from '../../../API/Category';
import { Edit } from '@mui/icons-material';
import BreadCrumb from '../../../common/components/Breadcrumb/index';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { DataGrid } from '@mui/x-data-grid';
import CategoryDialog from './CategoryDialog';

const breadcrumb = [
    {
        id: '01',
        breadItem: 'categories',
        link: '/dashboard/category'
    },
    {
        id: '02',
        breadItem: 'subcategory'
    }
];

const validationSchema = Yup.object({
    categoryName: Yup.string().required('Category Name is required'),
    categoryCode: Yup.number().typeError('Category Code must be a number')
        .test(
            'max-digits',
            'Category Code should be at most 8 digits',
            (value) => value !== null && value !== undefined && String(Math.floor(Math.abs(value))).length <= 8
        ).required('Category Code is required'),
});

const SubCategory = () => {
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [subCategories, setSubCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);

    const majorId = location.state.majorId;

    const handleEditCategory = (id) => {
        const selected = subCategories.find((c) => c._id === id);
        if (selected) {
            setSelectedCategory(selected);
            formik.setFieldValue('categoryName', selected.categoryName);
            formik.setFieldValue('categoryCode', selected.categoryCode);
            setOpen(true);
        }
    };

    const handleClickOpen = () => {
        setSelectedCategory(null);
        formik.setFieldValue('categoryName', '');
        formik.setFieldValue('categoryCode', '');
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedCategory(null);
        formik.setFieldValue('categoryName', '');
        formik.setFieldValue('categoryCode', '');
    };


    useEffect(
        () =>
            getSubCategoriesById(majorId).then((response) => {
                console.log('got sub categories', response);
                setSubCategories(response);
            }),
        [majorId, open]
    );

    const formik = useFormik({
        initialValues: {
            categoryName: '',
            categoryCode: '',
        },
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            if (selectedCategory) {
                await editSubCategoryById(selectedCategory._id, values);
            } else {
                await createSubCategory(values, majorId, setOpen);
            }
            setOpen(false);
            setSelectedCategory(null);
            formik.setFieldValue('categoryName', '');
            formik.setFieldValue('categoryCode', '');
        },
    });

    const columns = useMemo(() => [
        {
            field: 'categoryCode',
            headerName: 'Sub Category Code',
            flex: 1
        },
        {
            field: 'categoryName',
            headerName: 'Sub Category Name',
            flex: 2
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 1,
            sortable: false,
            renderCell: (params) => (
                <IconButton
                    onClick={(e) => {
                        e.stopPropagation();
                        handleEditCategory(params.row._id);
                    }}
                    aria-label="view"
                >
                    <Edit sx={{ fontSize: 20 }} />
                </IconButton>
            )
        }
    ], [subCategories]);

    return (
        <>
            <Box mb={1.5} mx={{ xs: 4, md: 9 }}>
                {' '}
                <BreadCrumb breadItems={breadcrumb} />
            </Box>
            <Box sx={{ mx: { xs: 4, md: 9 }, display: 'flex', rowGap: 2, alignItems: 'flex-end', flexDirection: 'column' }}>

                <Button
                    variant="contained"
                    onClick={handleClickOpen}
                    color="secondary"
                >
                    Add Sub Category
                </Button>


                <Box sx={{ height: 500, width: '100%' }}>
                    <DataGrid
                        rows={subCategories || []}
                        columns={columns}
                        pageSize={5}
                        rowsPerPageOptions={[5]}
                        getRowId={(row) => row?._id}
                        id={Math.random()}
                        disableSelectionOnClick
                    />
                </Box>
            </Box>

            <CategoryDialog open={open} onClose={handleClose} selectedCategory={selectedCategory} formik={formik} />

        </>
    );
};

export default SubCategory;
